import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post } from '@/lib/api'
import { getAuthToken, clearAuthToken } from '@/lib/auth-token'

import type { AuthUser } from '@company/shared'

/**
 * 认证相关 Query Keys
 */
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
}

/**
 * 获取当前用户信息
 */
export function useCurrentUser() {
  const token = getAuthToken()

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: () => get<AuthUser>('/auth/me'),
    staleTime: 5 * 60 * 1000, // 5 分钟
    retry: false,
    enabled: !!token,
  })
}

/**
 * 登出
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // 清除本地 token
      clearAuthToken()
      // 调用后端登出 API（可选，用于清除服务端会话）
      try {
        await post<{ redirectUrl: string }>('/auth/logout')
      } catch {
        // 忽略登出 API 错误
      }
      return { redirectUrl: '/login' }
    },
    onSuccess: (data) => {
      // 清除所有缓存
      queryClient.clear()
      // 重定向到登录页
      window.location.href = data.redirectUrl
    },
  })
}

/**
 * 检查是否已认证
 */
export function useIsAuthenticated() {
  const { data, isLoading, isError } = useCurrentUser()
  return {
    isAuthenticated: !!data && !isError,
    isLoading,
    user: data,
  }
}

/**
 * 获取当前认证用户（别名）
 */
export function useAuth() {
  const { data, isLoading, isError } = useCurrentUser()
  return {
    user: data,
    isLoading,
    isAuthenticated: !!data && !isError,
  }
}
